import React, { useEffect, useState } from 'react';
import OrganizationImages from '../OrganizationImages';
import { Globe, ExternalLink } from 'lucide-react';
import organizationsService from '../../services/organizationsService';
import { SOCIAL_NETWORKS, SOCIAL_KEYS, isAcceptableUrl } from '../../utils/organizationSocials';

/**
 * The organisations surface: a directory first, settings second.
 *
 * Three views, and the order matters. LIST answers "which organisation";
 * DETAIL answers "which team in it" — the question people actually arrive
 * with; SETTINGS is the form, reached deliberately rather than met on the way
 * in. The first version opened straight into the form, which is why it read as
 * a settings page that happened to contain teams.
 */
// Where the public profile lives. The desktop app has no window.location to
// derive this from, so it is stated once here rather than guessed per call
// site.
export const PUBLIC_SITE_URL = 'https://apiboo.com';

// ── Profile ──────────────────────────────────────────────────────────────────

export function ProfileTab({ org, canEdit, onSaved }) {
  const [draft, setDraft] = useState(org);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => setDraft(org), [org]);

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setSocial = (k, v) =>
    setDraft((d) => ({ ...d, socials: { ...(d.socials || {}), [k]: v } }));

  const badUrl =
    !isAcceptableUrl(draft.website_url) ||
    SOCIAL_KEYS.some((k) => !isAcceptableUrl(draft.socials?.[k]));

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await organizationsService.updateOrganization(org.id, {
        name: draft.name,
        description: draft.description,
        website_url: draft.website_url || null,
        socials: draft.socials || {},
        public_profile: !!draft.public_profile,
        billing_email: draft.billing_email || null,
      });
      await onSaved();
    } catch {
      setError('Could not save. Check the links and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="org-form">
      {/* Above the text fields: these are what the organisation LOOKS like,
          and they are the only part of this form a stranger ever sees, since
          the public profile renders them. */}
      <OrganizationImages org={org} canEdit={canEdit} onSaved={onSaved} />

      <label className="org-field">
        <span className="org-field-label">Name</span>
        <input
          className="org-input"
          value={draft.name || ''}
          disabled={!canEdit}
          onChange={(e) => setField('name', e.target.value)}
        />
      </label>

      <label className="org-field">
        <span className="org-field-label">Address</span>
        {/* Read-only, and said so rather than simply disabled: the slug is in
            public URLs from Phase 6, and one that can change is a link that
            can rot. The server refuses to cast it either way. */}
        <input className="org-input" value={`/orgs/${draft.slug || ''}`} readOnly />
        <span className="org-field-hint">Set when the organization was created; it cannot change.</span>
      </label>

      <label className="org-field">
        <span className="org-field-label">Description</span>
        <textarea
          className="org-input org-textarea"
          rows={3}
          value={draft.description || ''}
          disabled={!canEdit}
          onChange={(e) => setField('description', e.target.value)}
        />
      </label>

      <label className="org-field">
        <span className="org-field-label">Website</span>
        <input
          className={`org-input${isAcceptableUrl(draft.website_url) ? '' : ' org-input--bad'}`}
          value={draft.website_url || ''}
          placeholder="https://acme.com"
          disabled={!canEdit}
          onChange={(e) => setField('website_url', e.target.value)}
        />
      </label>

      <div className="org-field">
        <span className="org-field-label">Social links</span>
        {/* A picker over the server's allowlist, never a free-text network
            name: the server drops keys it does not know, so a typed one would
            silently vanish on save. */}
        {SOCIAL_KEYS.map((key) => (
          <div key={key} className="org-social-row">
            <span className="org-social-label">{SOCIAL_NETWORKS[key].label}</span>
            <input
              className={`org-input${isAcceptableUrl(draft.socials?.[key]) ? '' : ' org-input--bad'}`}
              value={draft.socials?.[key] || ''}
              placeholder={SOCIAL_NETWORKS[key].placeholder}
              disabled={!canEdit}
              onChange={(e) => setSocial(key, e.target.value)}
            />
          </div>
        ))}
      </div>

      <label className="org-check">
        {/* The app's own checkbox (`checkbox-brand`), same as the params
            table in collections — a raw browser checkbox is the one control
            that never matches the theme, in either mode. */}
        <input
          type="checkbox"
          className="checkbox-brand cursor-pointer"
          checked={!!draft.public_profile}
          disabled={!canEdit}
          onChange={(e) => setField('public_profile', e.target.checked)}
        />
        <span>
          <span className="org-check-title">
            <Globe size={13} aria-hidden /> Public profile page
          </span>
          <span className="org-check-hint">
            Lets anyone see this organization's name, description, logo and links at
            /orgs/{draft.slug}. Off by default — nothing about your teams, members or
            billing is ever shown.
          </span>
          {/* Only once it is actually published: the page 404s for everybody
              until then, including its own owner, so offering the link before
              the toggle is on would send people to a dead end. Reads from the
              SAVED org rather than the draft, because ticking the box does not
              publish anything until Save. */}
          {org.public_profile && (
            <button
              type="button"
              className="org-preview-link"
              onClick={() =>
                window.open(
                  `${PUBLIC_SITE_URL}/orgs/${org.slug}`,
                  '_blank',
                  'noopener,noreferrer'
                )
              }
            >
              <ExternalLink size={12} aria-hidden /> View public page
            </button>
          )}
        </span>
      </label>

      {error && <p className="org-error">{error}</p>}

      {canEdit && (
        <div className="org-form-actions">
          <button
            type="button"
            className="org-btn org-btn--primary"
            disabled={busy || badUrl}
            onClick={save}
          >
            Save changes
          </button>
          {badUrl && <span className="org-error">Links must start with http:// or https://</span>}
        </div>
      )}
    </div>
  );
}
